import React from 'react';
import {useForm} from 'react-hook-form';
import {ChevronDownIcon, CheckIcon} from '@heroicons/react/20/solid';
import {Menu, Transition} from '@headlessui/react';
import {Quill, QuillWrapper} from 'components/Ui';
import {
  ApproachPrelead,
  ApproachPreleadProject,
  Approach,
  useCreateCommentActivityMutation,
} from 'api';
import useApproachModalContext from './useApproachModalContext';
import ProjectSelect from './ProjectSelect';

interface Props {
  approach: Approach;
  onCompleted: () => void;
}

const CommentForm = ({approach, onCompleted}: Props) => {
  const {preleadProject} = useApproachModalContext();

  const {
    handleSubmit,
    setValue,
    watch,
    formState: {isSubmitted},
  } = useForm({
    defaultValues: {
      body: '',
      bodyPlainText: '',
      isCompleted: false,
    },
    mode: 'onChange',
  });

  const [create] = useCreateCommentActivityMutation({
    refetchQueries: ['approaches', 'approachCounts'],
    onCompleted: onCompleted,
  });

  const {isCompleted, body} = watch();

  const onSubmit = (values: any) => {
    const {isCompleted, body, bodyPlainText} = values;

    create({
      variables: {
        uuid: preleadProject?.uuid,
        approachUuid: approach?.uuid,
        isCompleted: isCompleted,
        attributes: {
          body: body,
          bodyPlainText: bodyPlainText,
        },
      },
    });
  };

  return (
    <form className="p-6 flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-1">
        <label className="text-c-light">アプローチ名</label>
        <span className="text-base">
          {approach?.approachableType === 'Approach::Prelead' &&
            (approach?.approachable as ApproachPrelead)?.approachGroupPrelead
              ?.title}
          {approach?.approachableType === 'Approach::PreleadProject' &&
            (approach?.approachable as ApproachPreleadProject)
              ?.approachGroupPreleadProject?.title}
        </span>
      </div>
      <div className="w-full flex flex-col gap-1">
        <label className="text-c-light">
          <span className="text-red-500">＊&nbsp;</span>営業リスト
        </label>
        <ProjectSelect />
      </div>
      <div>
        <QuillWrapper
          style={{
            borderRight: 'none',
            borderBottom: 'none',
            borderLeft: 'none',
            borderRadius: 0,
          }}
          className="
            mb-8
            [&_.ql-toolbar]:absolute
            [&_.ql-toolbar]:w-full
            [&_.ql-toolbar]:bottom-0
            [&_.ql-toolbar]:left-0
            [&_.ql-toolbar]:translate-y-full
            [&_.ql-editor]:max-h-80
        ">
          <Quill
            placeholder="コメント"
            theme="snow"
            onChange={(content, delta, source, editor) => {
              setValue('body', content);
              setValue('bodyPlainText', editor.getText());
            }}
            modules={{
              toolbar: [
                {header: [1, 2, 3, false]},
                'bold',
                'italic',
                'underline',
                'code-block',
                'link',
              ],
            }}
            bounds={'#quill-container'}
          />
          <div id="quill-container" />
        </QuillWrapper>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="submit"
          disabled={!preleadProject || !body || isSubmitted}
          className={`bg-[#61CF8D] text-white text-base font-medium py-2 w-72 flex items-center justify-center border-none cursor-pointer disabled:bg-c-bg disabled:text-c-light ${
            approach?.status === 'open' ? 'rounded-l' : 'rounded'
          }`}>
          {isCompleted
            ? 'アプローチを完了して登録'
            : 'アプローチを完了しないでログのみ登録'}
        </button>
        {approach?.status === 'open' && (
          <Menu as="div" className="relative inline-block text-left z-30">
            <Menu.Button
              disabled={!preleadProject || !body}
              className="bg-[#61CF8D] text-white text-base font-medium py-2 w-8 border-l border-white rounded-r cursor-pointer disabled:bg-c-bg disabled:text-c-light">
              <ChevronDownIcon className="w-4 h-4 relative top-1" />
            </Menu.Button>
            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95">
              <Menu.Items className="absolute right-0 w-80 origin-top-right rounded-b bg-white shadow-lg focus:outline-none z-30">
                <Menu.Item>
                  <div
                    className={`hover:bg-c-bg py-4 pl-2 pr-4 w-full flex items-center gap-2 ${
                      isCompleted ? 'bg-white' : 'bg-c-bg'
                    }`}>
                    <CheckIcon
                      className={`w-4 h-4 ${
                        isCompleted ? 'text-transparent' : 'text-[#61CF8D]'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setValue('isCompleted', false)}
                      className="cursor-pointer bg-transparent text-left text-base text-medium w-full hover:bg-c-bg flex flex-col gap-2 leading-none">
                      アプローチを完了しないでログのみ登録
                      <span className="text-xs">
                        アプローチは完了せずアプローチ一覧に残ります
                      </span>
                    </button>
                  </div>
                </Menu.Item>
                <Menu.Item>
                  <div
                    className={`hover:bg-c-bg py-4 pl-2 pr-4 w-full flex items-center gap-2 ${
                      isCompleted ? 'bg-c-bg' : 'bg-white'
                    }`}>
                    <CheckIcon
                      className={`w-4 h-4 ${
                        isCompleted ? 'text-[#61CF8D]' : 'text-transparent'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setValue('isCompleted', true)}
                      className="cursor-pointer bg-transparent text-left text-base text-medium w-full hover:bg-c-bg flex flex-col gap-2 leading-none">
                      アプローチを完了して登録
                      <span className="text-xs">
                        アプローチは完了しアプローチ一覧から削除されます。
                      </span>
                    </button>
                  </div>
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    </form>
  );
};

export default CommentForm;
